import {
  FieldInfo,
  hasFieldError,
  readOnlyTriggerClass,
  useFieldContext,
  useFormReadOnly,
  useIsFieldOptional,
} from './shared'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectGroup, SelectValue } from '../../ui/select'
import { Combobox, ComboboxContent, ComboboxItem, ComboboxList, ComboboxTrigger } from '../../ui/combobox'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip'
import { Button } from '../../ui/button'
import { FieldLabelRow } from './field-label-row'
import { InfoIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'

type Option = { value: string; label: string; description?: string }

export default function SimpleSelectField(props: {
  label: string
  options: Option[]
  className?: string
  orientation?: 'vertical' | 'horizontal'
  placeholder?: string
  optional?: boolean
  // Lock this single field: the same non-interactive read-only treatment as a whole read-only form.
  disabled?: boolean
}) {
  const field = useFieldContext<string>()
  const formReadOnly = useFormReadOnly() || !!props.disabled
  const isOptional = useIsFieldOptional(field.name, props.optional)
  const isHorizontal = props.orientation === 'horizontal'
  const hasDescriptions = props.options.some((o) => o.description)

  const selectedItem = props.options.find((o) => o.value === field.state.value) ?? null

  return (
    <div className={cn(props.className, formReadOnly && readOnlyTriggerClass)}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        {props.label && (
          <FieldLabelRow
            label={props.label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional={isHorizontal}
            className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
          />
        )}
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          {hasDescriptions ? (
            <>
              <Combobox
                items={props.options}
                value={selectedItem}
                onValueChange={(item: Option | null) => {
                  if (item) field.handleChange(item.value)
                }}
                isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
                filter={() => true}
              >
                <ComboboxTrigger
                  render={
                    <Button
                      id={field.name}
                      type="button"
                      variant="outline"
                      aria-invalid={hasFieldError(field.state.meta)}
                      className={cn('h-8 w-full justify-between font-normal', !selectedItem && 'text-muted-foreground')}
                    />
                  }
                >
                  {selectedItem ? selectedItem.label : <span>{formReadOnly ? '-' : props.placeholder}</span>}
                </ComboboxTrigger>
                <ComboboxContent className="min-w-(--anchor-width)">
                  <ComboboxList>
                    {(item: Option) => (
                      <ComboboxItem key={item.value} value={item}>
                        <span className="flex items-center gap-2">
                          {item.label}
                          {item.description && (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <span tabIndex={-1} className="inline-flex" onClick={(e) => e.stopPropagation()} />
                                }
                              >
                                <InfoIcon className="text-muted-foreground/50 size-3.5" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                {item.description}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </span>
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
              <FieldInfo fieldState={field.state} />
            </>
          ) : (
            <>
              {/* Ignore the empty-string change Radix Select spuriously emits while its controlled
                  `value` transitions from '' to a real value (async defaultValues). There is no empty
                  option, so a genuine selection is never '' — letting it through would clear the field
                  and mark it touched, blocking the form from re-syncing the loaded value. */}
              <Select
                value={field.state.value ?? ''}
                onValueChange={(val) => {
                  if (val != null && val !== '') field.handleChange(val)
                }}
              >
                <SelectTrigger
                  id={field.name}
                  aria-invalid={hasFieldError(field.state.meta)}
                  tabIndex={formReadOnly ? -1 : undefined}
                  className="w-full"
                >
                  {/* Render the label explicitly — Radix only surfaces the selected item's text via
                      a portal from the mounted item, but the items live in lazily-mounted
                      SelectContent (closed at mount), so a value from defaultValues showed an empty
                      trigger until first open. Passing children makes Radix render them directly. */}
                  <SelectValue placeholder={formReadOnly ? '-' : props.placeholder}>{selectedItem?.label}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {props.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FieldInfo fieldState={field.state} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
